import {
	Component,
	Input,
	OnChanges,
	OnInit,
	SimpleChanges
} from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { PLAYER_USER_PREFERENCE_KEY } from 'vbrick-player-src/shared/userPreference/PlayerUserPreferenceHandler';
import { PlayerUserPreferencesService } from 'vbrick-player-src/shared/userPreference/PlayerUserPreference.Service';
import { VbNestedMenuComponent } from './shared/VbNestedMenuComponent';
import { VideogularComponent } from './videogular/Videogular.Component';

import { HlsUtilService } from './HlsUtil.Service';
import { IVbSource } from './IVbSource';
import { userAgentUtil } from './UserAgentUtil';

function checkBrowserSupport(): boolean {
	const video: HTMLVideoElement = document.createElement('video');

	return typeof video.playbackRate === 'number' &&
		//Android chrome currently does not support playback rate
		!userAgentUtil.isAndroidChrome;
}

@Component({
	selector: 'vb-playback-rate-button',
	host: {
		'[class]': 'menuButtonStyles.textMenuButtonContainer'
	},
	template: `
		<ng-container *ngIf="(playbackRateEnabled$ | async) as playbackRateEnabled">
			<div
				[ngClass]="menuButtonStyles.dropdown"
				dropdown
				#dropdown="bs-dropdown"
				[autoClose]="true"
				[insideClick]="false"
				placement="top right"
				[dropup]="true"
				(isOpenChange)="onMenuOpenChange($event)">

				<ul
					[ngClass]="menuButtonStyles.dropdownMenu"
					class="playerSettingsSubmenu"
					*dropdownMenu
					#vbDropdownMenu
					aria-labelledby="buttonMenuPlaybackRate"
					id="dropdownPlaybackRateMenu"
					role="menu"
					tabindex="0">
					<li role="none">
						<button type="button"
							role="menuitem"
							class="dropdown-item btn-link"
							[ngClass]="menuButtonStyles.backToMainMenuItem"
							(click)="hideMenu()">
							<span>{{ translations.playbackSpeed }}</span>
						</button>
					</li>
					<li role="none" class="divider dropdown-divider"></li>
					<li role="none"
						*ngFor="let rate of playbackRates">
						<button type="button"
role="menuitem"
							class="dropdown-item btn-link"
							(click)="onSelectPlaybackRate(rate)"
							[ngClass]="vgApi.playback === rate ? menuButtonStyles.selected : ''">
							<span *ngIf="rate === 1">{{ translations.normal }}</span>
							<span *ngIf="rate !== 1">{{ rate | number }}x</span>
						</button>
					</li>
				</ul>
			</div>
		</ng-container>
`,
    standalone: false
})
export class VbPlaybackRateButtonComponent extends VbNestedMenuComponent implements OnChanges, OnInit {
	@Input() public activeSource: any;

	public isMobile: boolean;
	public readonly playbackRates: number[] = [1, 1.25, 1.5, 2];
	public readonly browserSupportsPlaybackRate = checkBrowserSupport();

	private playbackRateEnabledSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public playbackRateEnabled$: Observable<boolean> = this.playbackRateEnabledSubject$.asObservable();

	constructor(
		private HlsUtilService: HlsUtilService,
		private readonly PlayerUserPreferencesService: PlayerUserPreferencesService,
		public vgApi: VideogularComponent
	) {
		super();
	}

	public ngOnInit(): void {
		this.isMobile = userAgentUtil.isMobile;

		this.updateVideoSource(this.activeSource);

		this.vgApi.mediaElement.addEventListener('ratechange', () => {
			//IE resets playbackRate to 1 when playing/pausing, seeking, or switching video source
			if (this.vgApi.rawMediaElement.playbackRate !== this.vgApi.playback) {
				this.applyPlaybackRate();
			}
		});
	}

	public ngOnChanges(changes: SimpleChanges): void {
		super.ngOnChanges(changes);

		if (changes.activeSource) {
			this.updateVideoSource();
		}
	}

	private applyPlaybackRate(): void {
		this.setPlaybackRate(this.vgApi.playback);
	}

	private isNativeVideoElement(): boolean {
		return !this.vgApi.isShimmedMediaElement();
	}

	public playbackRatesTrackByFunc(index: number) {
		return index;
	}

	public setPlaybackRate(val: number): void {
		this.vgApi.setPlayback(val || 1);
	}

	public onSelectPlaybackRate(val: number): void {
		this.setPlaybackRate(val);
		this.PlayerUserPreferencesService.setPreference<number>(PLAYER_USER_PREFERENCE_KEY.PLAYBACK_RATE, this.vgApi.playback);
		this.hideMenu();
	}

	private sourceSupportsPlaybackRate(source: any): boolean {
		return source.type === 'video/mp4' ||
			this.HlsUtilService.isHLS(source.src, source.type);
	}

	private updateVideoSource(source?: any): void {
		const playbackRateEnabled = this.browserSupportsPlaybackRate &&
			source &&
			!source.isLive &&
			this.isNativeVideoElement() &&
			this.sourceSupportsPlaybackRate(source);

		this.playbackRateEnabledSubject$.next(playbackRateEnabled);

		if (playbackRateEnabled) {
			const userPreferencePlaybackRate = this.PlayerUserPreferencesService.getPreference<number>(PLAYER_USER_PREFERENCE_KEY.PLAYBACK_RATE);
			this.setPlaybackRate(userPreferencePlaybackRate || 1);
		}
	}
}
